"use client";

import { ArrowUpRight, Film, Drama, Tv, Activity, Wrench, Palette } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const labs = [{
  id: "SCENE-01",
  name: "Film Performance",
  description: "Immersive character studies for the silver screen, focusing on psychological depth and cinematic presence.",
  tags: ["#METHOD", "#SCREEN-CRAFT", "#NOIR"],
  accentVar: "--chart-1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_still.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_still.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_film_still.webp-wvc-sizes",
  icon: Film
}, {
  id: "SCENE-02",
  name: "Classical Theater",
  description: "Commanding the stage through vocal resonance, physical precision, and the weight of dramatic tradition.",
  tags: ["#SHAKESPEARE", "#STAGE-CRAFT", "#REPERTORY"],
  accentVar: "--chart-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theater_stage.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theater_stage.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_theater_stage.png-wvc-sizes",
  icon: Drama
}, {
  id: "SCENE-03",
  name: "Television Drama",
  description: "Dynamic episodic storytelling, adapting to the fast-paced rhythm of high-end streaming and network series.",
  tags: ["#EPISODIC", "#CHARACTER-ARC", "#ENSEMBLE"],
  accentVar: "--chart-3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_tv_production.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_tv_production.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_tv_production.png-wvc-sizes",
  icon: Tv
}, {
  id: "SCENE-04",
  name: "Physical Statistics",
  description: "A disciplined approach to the physical form, maintaining the versatility required for demanding action roles.",
  tags: ["#ATHLETICISM", "#STUNTS", "#PRESENCE"],
  accentVar: "--chart-4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_athletic_training.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_athletic_training.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_athletic_training.png-wvc-sizes",
  icon: Activity
}, {
  id: "SCENE-05",
  name: "Specialized Skills",
  description: "A diverse toolkit including stage combat, multiple dialects, and professional-grade musical proficiency.",
  tags: ["#DIALECTS", "#COMBAT", "#VOCALS"],
  accentVar: "--chart-1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_skills_rehearsal.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_skills_rehearsal.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_skills_rehearsal.png-wvc-sizes",
  icon: Wrench
}, {
  id: "SCENE-06",
  name: "Creative Direction",
  description: "Collaborating with directors and cinematographers to craft the visual language of the protagonist.",
  tags: ["#COLLABORATION", "#VISION", "#STORYTELLING"],
  accentVar: "--chart-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_creative_direction.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_creative_direction.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_creative_direction.png-wvc-sizes",
  icon: Palette
}];
export default function ArtistryAttributes() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="530" id="artistry-attributes" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-36" style={{
    backgroundImage: "radial-gradient(ellipse at center, var(--background) 0%, hsl(0 0% 2%) 100%)"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-12">
          </div>
          <div className="lg:col-span-10 lg:col-start-2">
            <h2 className="font-serif font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] tracking-tight text-foreground">
              Six disciplines.
              <br />
              <span className="text-muted-foreground opacity-50">One singular vision.</span>
            </h2>
          </div>
        </div>

        {}
        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2 lg:grid-cols-3">
          {labs.map((lab, i) => {
          const Icon = lab.icon;
          return <article key={lab.id} data-index={i} className={`group relative flex flex-col bg-card border border-border transition-all duration-700 ease-out hover:border-primary md:h-[480px] rounded-md ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
            "--lab-accent": `var(${lab.accentVar})`
          }}>
                {}
                <div className="relative h-48 sm:h-52 overflow-hidden">
                  <div className="absolute inset-0 transition-all duration-700 ease-out group-hover:opacity-20" style={{
                background: `linear-gradient(135deg, var(--foreground) 0%, var(--background) 100%)`,
                opacity: 0
              }} />
                  <img src={lab.image} data-wvc-srcset={lab.srcset} data-wvc-sizes={lab.sizes} alt={lab.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover grayscale opacity-40 transition-all duration-700 ease-out group-hover:grayscale-0 group-hover:opacity-80" />
                  <div className="absolute top-4 right-4">
                    <Icon className="h-5 w-5 text-muted-foreground transition-colors duration-500 group-hover:text-primary" strokeWidth={1.25} />
                  </div>
                </div>

                {}
                <div className="relative flex flex-1 flex-col p-6 sm:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs tracking-widest text-muted-foreground transition-colors duration-500">
                      [{lab.id}]
                    </span>
                    <span className="font-mono text-xs text-muted-foreground/40">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="font-serif font-default mt-4 text-2xl sm:text-3xl leading-tight tracking-tight text-foreground">
                    {lab.name}
                  </h3>

                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground font-sans">
                    {lab.description}
                  </p>

                  <ul className="mt-5 flex flex-wrap gap-2">
                    {lab.tags.map((tag, ti) => <li key={ti} data-index={ti} className="font-mono text-[10px] tracking-wider text-muted-foreground border border-border px-2 py-1 rounded-md transition-colors duration-300 group-hover:text-foreground group-hover:border-muted-foreground">
                        {tag}
                      </li>)}
                  </ul>

                  {}
                  <div className="mt-auto overflow-hidden pt-6">
                    <Link to="/TheSpotlightPage" className="font-mono text-xs tracking-widest uppercase flex items-center gap-2 translate-y-8 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 text-primary">
                      <span>VIEW CREDITS</span>
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.25} />
                    </Link>
                  </div>
                </div>

                {}
                <span aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-3 w-3 border-t border-l border-primary opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r border-primary opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </article>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button asChild variant="ghost" className="font-mono text-xs sm:text-sm tracking-widest uppercase text-foreground hover:bg-transparent hover:text-primary group/btn px-0 rounded-md">
            <Link to="/TheLedgerPage" className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-border transition-all duration-500 group-hover/btn:w-16 group-hover/btn:bg-primary" />
              <span>DOWNLOAD FULL RESUME</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" strokeWidth={1.25} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}